// useSessionLearned — lê o que a sessão ENSINOU ao Apolo (self-improvement por
// sessão) pra uma pill discreta no chat.
//
// O learning por-sessão (runSessionLearning no Worker) roda no fim de cada turno,
// e quando aprende algo grava um session_event type='session_learned'
// { detected, promoted }. Antes isso só ia pro Discord — o app não mostrava nada.
// Este hook soma os session_learned da sessão (GET /api/learning/events) e a pill
// avisa. Refetch no fim do turno (o learning é fire-and-forget/assíncrono, então
// esperamos alguns segundos antes de reler). READ-ONLY, best-effort.

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api.js';

export function useSessionLearned(sessionId, running) {
  const [learned, setLearned] = useState(null); // { detected, promoted, at } | null

  const load = useCallback(async () => {
    if (!sessionId) { setLearned(null); return; }
    const res = await apiFetch(
      `/api/learning/events?session_id=${encodeURIComponent(sessionId)}&limit=100`,
    ).catch(() => ({ ok: false }));
    if (!res?.ok) return;
    const rows = Array.isArray(res.data?.events) ? res.data.events : Array.isArray(res.data) ? res.data : [];
    const ev = rows.filter((r) => r?.type === 'session_learned');
    if (ev.length === 0) { setLearned(null); return; }
    let detected = 0;
    let promoted = 0;
    for (const r of ev) {
      const d = r.data || {};
      detected += Number(d.detected) || 0;
      promoted += Number(d.promoted) || 0;
    }
    // rows vêm DESC → ev[0] é o mais recente.
    setLearned({ detected, promoted, at: ev[0].created_at || null });
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  // O learning grava DEPOIS do turno (fire-and-forget) — quando o turno termina
  // (running true→false), relê ~4s depois pra pegar o session_learned fresco.
  const prevRunning = useRef(running);
  useEffect(() => {
    if (prevRunning.current && !running) {
      const t = setTimeout(load, 4000);
      prevRunning.current = running;
      return () => clearTimeout(t);
    }
    prevRunning.current = running;
    return undefined;
  }, [running, load]);

  return learned;
}
